import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { set } from 'ember-set-helper';
import { gt } from 'ember-truth-helpers';
import BvEmptySearchResult from 'volta/components/bv-empty-search-result';
import BvIcon from 'volta/components/bv-icon';
import BvTextField from 'volta/components/bv-text-field';
import FiltersBaseFilter from 'volta/components/filters/base-filter';
import getStrict from 'volta/helpers/get-strict';
import { parseCFApiParamKey } from 'volta/models/custom-field-definition';
import { indexByFn } from 'volta/utils/array-utils';
import { lower, sanitized } from 'volta/utils/text-utils';

import type { IntlService } from 'ember-intl';
import type { IResourceName } from 'volta/models/base-model';
import type { ICollectionFilter } from 'volta/models/types/collection-view';
import type LabelService from 'volta/services/label-service';

interface CollectionViewFiltersSignature {
  Args: {
    filters: TFilterDefinition[];
    filterValues: ICollectionFilter[];
    resourceName: IResourceName;
    onChange: (filterValue: TFilterValue, queryParam: string) => void;
  };
}

export default class CollectionViewFiltersComponent extends Component<CollectionViewFiltersSignature> {
  @service intl!: IntlService;
  @service labelService!: LabelService;

  @tracked search?: string;

  get filtersWithTitle() {
    return this.args.filters.reduce((acc: (TFilterDefinition & { title: string })[], p) => {
      const labelKey =
        (p.customFieldDefinition && parseCFApiParamKey(p.queryParam)) || p.queryParam;
      const title = (
        p.customFieldDefinition
          ? this.labelService.tOrElse(labelKey, p.title)
          : (p.title ??
            this.labelService.tOrElse(
              p.queryParam && 'filterDefinitions.' + p.queryParam,
              this.intl.t(p.queryParam)
            ))
      ) as string;
      if (!p.hidden) {
        acc.push({ ...p, title });
      }

      return acc;
    }, []);
  }

  @cached
  get filteredFilters() {
    const { search, filtersWithTitle } = this;
    if (!search || search === '') {
      return filtersWithTitle;
    }
    const _search = sanitized(search);
    return filtersWithTitle.filter((p) => {
      return p.title && sanitized(p.title as string).includes(_search);
    });
  }

  @cached
  get indexedFilterValues() {
    return indexByFn(this.args.filterValues, (f) => f.property);
  }

  // helpers
  initialValue(filters: ICollectionFilter[] = [], queryParam: string) {
    return filters.find((filter: ICollectionFilter) => filter.property === queryParam);
  }

  <template>
    <BvTextField
      class='u-flex-fill u-p-3'
      @labelHidden={{true}}
      @placeholder={{t 'search'}}
      @value={{this.search}}
      @prefix={{component BvIcon glyph='search'}}
      @onChange={{set this 'search'}}
      @clearButton={{gt this.search.length 0}}
      @onClearButtonClick={{set this 'search' undefined}}
    />
    {{#each this.filteredFilters key='apiParam' as |filter|}}
      {{#unless filter.hidden}}
        <div class='collection-view__filter u-p-3 u-border-top-faint'>
          <FiltersBaseFilter
            {{! @glint-expect-error: ICollectionFilter is a adjacent type of FilterValue}}
            @filterValues={{@filterValues}}
            @definition={{filter}}
            @resourceName={{@resourceName}}
            @initialValue={{getStrict this.indexedFilterValues filter.queryParam}}
            @onChange={{@onChange}}
            @clearDefaultValue={{filter.clearDefaultValue}}
          />
        </div>
      {{/unless}}
    {{else}}
      <BvEmptySearchResult
        class='u-px-3 u-py-8'
        @title={{t 'bvList.emptySearchResultTitle' resourceNamePlural=(lower (t 'filters'))}}
      />
    {{/each}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'CollectionView::Filters': typeof CollectionViewFiltersComponent;
    'collection-view/filters': typeof CollectionViewFiltersComponent;
  }
}
